function toInt(value) {
  return Number(value || 0);
}

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function percent(value, total) {
  if (!total) {
    return "0%";
  }
  return `${Math.round((Number(value || 0) / total) * 100)}%`;
}

export function buildManagerDeckSnapshot(dashboard = {}) {
  const workloadUsers = ((dashboard.workload || {}).users || []).slice();
  const spotlight = dashboard.spotlight || {};
  const mostLoadedUser = spotlight.most_loaded_user || workloadUsers[0] || null;
  const maintenanceRuns = ((dashboard.maintenance || {}).runs || []).length;
  return {
    attentionRequired: Boolean(spotlight.attention_required),
    mostLoadedUser,
    overdueCount: toInt((dashboard.sla || {}).overdue_total),
    firstResponseOverdueCount: toInt((dashboard.sla || {}).first_response_overdue_total),
    unassignedCount: toInt((dashboard.summary || {}).open_unassigned),
    maintenanceRuns,
    overdueSpotlight: (spotlight.overdue_conversations || []).length,
    unassignedSpotlight: (spotlight.unassigned_conversations || []).length,
  };
}

export function buildManagerCommandWorkspace(dashboard = {}, actions = [], options = {}) {
  const workloadUsers = ((dashboard.workload || {}).users || [])
    .slice()
    .sort((left, right) => toInt(right.open_assigned_count) - toInt(left.open_assigned_count));
  const spotlight = dashboard.spotlight || {};
  const reports = dashboard.reports || {};
  const dayTotals = (reports.day || {}).totals || {};
  const weekTotals = (reports.week || {}).totals || {};
  const manualActions = (actions || []).filter((item) => item.source_kind !== "automation");
  const automationActions = (actions || []).filter((item) => item.source_kind === "automation");
  const mostLoadedUser = spotlight.most_loaded_user || workloadUsers[0] || null;
  const activeView = options.activeView || "open";
  const atRiskTotal = toInt((dashboard.sla || {}).overdue_total) + toInt((dashboard.summary || {}).open_unassigned);
  const dispositionTotal = toInt(weekTotals.disposition_total);
  const outcomeFocusRows = Object.entries(weekTotals.disposition_counts || {})
    .sort((left, right) => toInt(right[1]) - toInt(left[1]))
    .slice(0, 4)
    .map(([key, count], index) => ({
      label: titleCase(key),
      value: toInt(count),
      tone: index === 0 ? "ready" : index === 1 ? "stale" : "muted",
      meta: `${percent(count, dispositionTotal)} of ${dispositionTotal || 0} outcomes this window`,
    }));
  return {
    reviewCards: [
      {
        label: "Today inbound",
        value: toInt(dayTotals.inbound_volume),
        detail: `${toInt(weekTotals.inbound_volume)} inbound this week`,
        tone: toInt(dayTotals.inbound_volume) > 0 ? "ready" : "muted",
      },
      {
        label: "At-risk queue",
        value: atRiskTotal,
        detail: `${toInt((dashboard.sla || {}).overdue_total)} overdue | ${toInt((dashboard.summary || {}).open_unassigned)} unassigned`,
        tone: atRiskTotal > 0 ? "overdue" : "ready",
      },
      {
        label: "Manual oversight",
        value: manualActions.length,
        detail: `${automationActions.length} automation events in the same feed`,
        tone: manualActions.length > 0 ? "stale" : "muted",
      },
    ],
    commandCards: [
      {
        key: "overdue",
        label: "Overdue queue",
        value: toInt((dashboard.sla || {}).overdue_total),
        detail: `${toInt((dashboard.sla || {}).first_response_overdue_total)} first-response overdue`,
        tone: toInt((dashboard.sla || {}).overdue_total) > 0 ? "overdue" : "ready",
        actionLabel: "Review overdue",
        action: { type: "view", viewKey: "overdue" },
      },
      {
        key: "unassigned",
        label: "Unassigned queue",
        value: toInt((dashboard.summary || {}).open_unassigned),
        detail: `${(spotlight.unassigned_conversations || []).length} spotlighted now`,
        tone: toInt((dashboard.summary || {}).open_unassigned) > 0 ? "stale" : "ready",
        actionLabel: "Review unassigned",
        action: { type: "view", viewKey: "unassigned" },
      },
      {
        key: "most_loaded",
        label: "Most loaded user",
        value: mostLoadedUser ? mostLoadedUser.full_name : "None",
        detail: mostLoadedUser ? `${mostLoadedUser.open_assigned_count} open assigned` : "No active user focus",
        tone: mostLoadedUser && toInt(mostLoadedUser.open_assigned_count) > 0 ? "stale" : "muted",
        actionLabel: mostLoadedUser ? "Inspect workload" : "No user focus",
        action: mostLoadedUser ? { type: "user", userId: mostLoadedUser.user_id } : null,
      },
      {
        key: "manual_actions",
        label: "Recent manual actions",
        value: manualActions.length,
        detail: `${automationActions.length} automation action(s) in the same feed`,
        tone: manualActions.length > 0 ? "ready" : "muted",
        actionLabel: manualActions[0] ? "Open latest action" : "No action history",
        action: manualActions[0] ? { type: "conversation", conversationId: manualActions[0].conversation_id } : null,
      },
    ],
    workloadRows: workloadUsers.map((item) => ({
      userId: item.user_id,
      fullName: item.full_name,
      email: item.email,
      role: item.role,
      openAssignedCount: toInt(item.open_assigned_count),
      tone: toInt(item.open_assigned_count) > 0 ? "ready" : "muted",
      shareLabel: percent(item.open_assigned_count, toInt((dashboard.summary || {}).open_total)),
      capacityLabel:
        toInt(item.open_assigned_count) >= 4
          ? "Heavy load"
          : toInt(item.open_assigned_count) > 0
            ? "Active load"
            : "Capacity open",
    })),
    actionRows: (actions || []).slice(0, 6).map((item) => ({
      conversationId: item.conversation_id,
      contactName: item.contact_name,
      summary: item.summary,
      sourceKind: item.source_kind,
      occurredAt: item.occurred_at,
      tone: item.source_kind === "automation" ? "stale" : "ready",
      emphasis: item.source_kind === "automation" ? "Automation" : "Manual",
    })),
    outcomeFocusRows,
    rebalanceRows: [
      {
        label: "Primary queue focus",
        value: activeView,
        tone: activeView === "overdue" ? "overdue" : activeView === "unassigned" ? "stale" : "ready",
        meta: activeView === "overdue" ? "Escalate or release at-risk work first." : "Use the active slice to direct the next bulk action.",
      },
      {
        label: "Recommended target",
        value: mostLoadedUser ? mostLoadedUser.full_name : "No active assignee",
        tone: mostLoadedUser ? "ready" : "muted",
        meta: mostLoadedUser ? `${mostLoadedUser.role} holds the heaviest current load.` : "No workload recommendation available.",
      },
      {
        label: "Bulk recommendation",
        value:
          toInt((dashboard.summary || {}).open_unassigned) > 0
            ? "Assign unassigned work"
            : toInt((dashboard.sla || {}).overdue_total) > 0
              ? "Escalate overdue work"
              : "Release or rebalance selectively",
        tone:
          toInt((dashboard.sla || {}).overdue_total) > 0
            ? "overdue"
            : toInt((dashboard.summary || {}).open_unassigned) > 0
              ? "stale"
              : "ready",
        meta: "Use quick drilldowns to prepare the target slice before running a bulk action.",
      },
    ],
  };
}

export function summarizeBulkSelection(options = {}) {
  const settings = Object.assign({ selectedCount: 0, activeView: null }, options);
  const activeView = settings.activeView || "open";
  const recommendationByView = {
    open: "Use bulk actions only after reviewing each selected thread.",
    overdue: "Escalate or release overdue conversations deliberately; these are already at risk.",
    unassigned: "Assign is the safest default for unassigned conversations.",
    mine: "Release or escalate conversations from your own workload after review.",
  };
  const recommendedActionByView = {
    overdue: "escalate",
    unassigned: "assign",
    mine: "release",
    open: "assign",
  };
  return {
    selectedCount: settings.selectedCount,
    activeView,
    recommendedAction: recommendedActionByView[activeView] || "assign",
    recommendation: recommendationByView[activeView] || recommendationByView.open,
  };
}

export function summarizeBulkResult(action, payload = {}) {
  const summaryByAction = {
    assign: {
      count: toInt(payload.assigned_count),
      skipped: toInt(payload.skipped_count),
      label: "assigned",
    },
    release: {
      count: toInt(payload.released_count),
      skipped: toInt(payload.skipped_count),
      label: "released",
    },
    escalate: {
      count: toInt(payload.escalated_count),
      skipped: toInt(payload.skipped_count),
      label: "escalated",
    },
  };
  const summary = summaryByAction[action] || { count: 0, skipped: 0, label: action };
  const firstSkip = (payload.results || []).find((item) => item.status !== "success") || null;
  return {
    action,
    count: summary.count,
    skipped: summary.skipped,
    label: summary.label,
    firstSkipMessage: firstSkip ? firstSkip.message || firstSkip.status : null,
  };
}
